/**
 * Runtime configuration, parsed once at module load.
 *
 * Invariant: no secret is ever read here. `services/api` holds every OAuth
 * token and signing key; the browser only ever learns an origin and, in local
 * development, which user it is asserting (CLAUDE.md §1.6).
 */

/**
 * The fallback for how long the server keeps an `Idempotency-Key` and its
 * answer: the default of `RETENTION_IDEMPOTENCY_KEYS_HOURS` (`gp_persistence::
 * idempotency::DEFAULT_TTL_HOURS`, 24 h). The deployment's own value comes
 * from the session (`GET /me` → `idempotency_key_retention_hours`, ADR-074)
 * and is what the step-up list uses (`idempotencyKeyLifetimeMs` in
 * `app/auth/stepUpStore.ts`); this constant applies only when a server older
 * than that field leaves it out. A constant, not a `VITE_*` variable. A
 * waiting step-up action is shown as expired, and never sent again, once it
 * is as old as the lifetime in force: past it, the same key no longer replays
 * a request that did commit, so sending it could run the action twice.
 */
export const IDEMPOTENCY_KEY_LIFETIME_MS = 24 * 60 * 60 * 1000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface WebEnv {
  readonly apiBaseUrl: string;
  /** Identity asserted through `x-gp-dev-user`; only honoured by a local API. */
  readonly devUserId: string | null;
  /** Workspace the shell opens on. There is no `/me` endpoint yet. */
  readonly workspaceId: string | null;
  readonly mswEnabled: boolean;
  /**
   * A Vite dev build (`import.meta.env.DEV`). Loosens only what local
   * development genuinely needs — e.g. a plain-`http:` preview image from a
   * brand property served by a local dev server — never a check that guards
   * a permission, an approval or an external effect.
   */
  readonly localDev: boolean;
  /**
   * Build-time fallback for the `chinese_ui` flag (design doc §24.4).
   *
   * The server is authoritative: `GET /me` returns `features.chinese_ui`, read
   * from `FEATURE_CHINESE_UI`, and {@link resolveChineseUi} prefers it. This
   * value (`VITE_FEATURE_CHINESE_UI`, on unless explicitly turned off) only
   * applies before the session has loaded or against a server that does not
   * send the flag — a dev convenience, never an override. It only hides a
   * language; it never gates a permission or a check (CLAUDE.md §1.7).
   */
  readonly chineseUi: boolean;
  /**
   * The deployment's own asset origin (`VITE_ASSET_ORIGIN`), trusted for
   * Content Studio preview images alongside the brand's verified properties.
   * `null` when unset or invalid: an invalid value is never trusted.
   */
  readonly assetOrigin: AssetOrigin | null;
}

/**
 * A single parsed origin the reviewer's browser may fetch preview images from.
 *
 * Invariant: `protocol` is `https:`, or `http:` only when parsed in a local
 * dev build; `hostname` is lower-case without a trailing dot; there is no
 * path, query, fragment or credential — it names an origin, not a prefix.
 */
export interface AssetOrigin {
  readonly protocol: 'https:' | 'http:';
  readonly hostname: string;
  /** The explicit port, or `''` for the scheme's default (as `URL.port`). */
  readonly port: string;
  /** Serialised origin, e.g. `https://assets.example.com`. */
  readonly origin: string;
}

/** Why a configured `VITE_ASSET_ORIGIN` was ignored. */
export type AssetOriginProblem = 'not_a_url' | 'insecure_scheme' | 'not_an_origin' | 'credentials';

/**
 * Parses `VITE_ASSET_ORIGIN`. Pure: the caller decides whether to warn.
 *
 * Accepts exactly one absolute origin (a trailing `/` is tolerated). Anything
 * with a path, query, fragment or `user:pass@` is refused rather than
 * truncated, so a mistyped value never silently trusts a different origin.
 */
export function parseAssetOrigin(
  raw: string | undefined,
  localDev: boolean,
): { readonly origin: AssetOrigin | null; readonly problem: AssetOriginProblem | null } {
  const text = raw?.trim() ?? '';
  if (!text) return { origin: null, problem: null };
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return { origin: null, problem: 'not_a_url' };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { origin: null, problem: 'not_a_url' };
  }
  if (url.protocol === 'http:' && !localDev) {
    return { origin: null, problem: 'insecure_scheme' };
  }
  if (url.username || url.password) return { origin: null, problem: 'credentials' };
  if (url.pathname !== '/' || url.search || url.hash || text.endsWith('?') || text.endsWith('#')) {
    return { origin: null, problem: 'not_an_origin' };
  }
  const hostname = url.hostname.toLowerCase().replace(/\.+$/, '');
  if (!hostname) return { origin: null, problem: 'not_a_url' };
  return {
    origin: { protocol: url.protocol, hostname, port: url.port, origin: url.origin },
    problem: null,
  };
}

let assetOriginWarned = false;

/** One console warning per page load, and only in a dev build. */
function warnAssetOrigin(problem: AssetOriginProblem, localDev: boolean): void {
  if (!localDev || assetOriginWarned) return;
  assetOriginWarned = true;
  console.warn(
    `VITE_ASSET_ORIGIN ignored (${problem}): expected one https origin such as ` +
      '`https://assets.example.com` (http only in a dev build). No asset origin is trusted.',
  );
}

function readEnv(source: ImportMetaEnv): WebEnv {
  const devUserId = source.VITE_DEV_USER_ID?.trim() ?? '';
  const workspaceId = source.VITE_WORKSPACE_ID?.trim() ?? '';
  const localDev = source.DEV === true;
  const asset = parseAssetOrigin(source.VITE_ASSET_ORIGIN, localDev);
  if (asset.problem) warnAssetOrigin(asset.problem, localDev);
  return {
    apiBaseUrl: (source.VITE_API_BASE_URL?.trim() || 'http://localhost:8080').replace(/\/+$/, ''),
    devUserId: UUID.test(devUserId) ? devUserId : null,
    workspaceId: UUID.test(workspaceId) ? workspaceId : null,
    mswEnabled: source.VITE_ENABLE_MSW === '1',
    localDev,
    chineseUi: !['false', '0', 'off'].includes(
      (source.VITE_FEATURE_CHINESE_UI ?? '').trim().toLowerCase(),
    ),
    assetOrigin: asset.origin,
  };
}

export const env: WebEnv = readEnv(import.meta.env);

/**
 * Whether the zh-CN interface is offered: the server's `features.chinese_ui`
 * when the session carries it, otherwise the build-time fallback.
 */
export function resolveChineseUi(
  server: boolean | null | undefined,
  fallback: boolean = env.chineseUi,
): boolean {
  return typeof server === 'boolean' ? server : fallback;
}

export { readEnv };
